import { CompactPagination } from "@/components/Pagination";
import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import {
  ScrollContainer,
  WidgetContainer,
} from "@/features/workspace/widgets/Components";
import {
  type WidgetSetting,
  type WidgetSize,
  type WorkOrderWidgetList,
  useWorkOrderSearch,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { useEffect, useMemo, useState } from "react";

import { CARD_HEIGHT_NORMAL, CARD_HEIGHT_TALL } from "../constants";
import { mapInstallationQuotationData } from "../installationQuotations/utils";
import { WorkOrdersList } from "./WorkOrdersList";
import { getWorkOrderWidgetSettings } from "./utils";

interface WorkOrderWidgetProps {
  title: string;
  settings?: WidgetSetting;
  widgetSize: WidgetSize;
}

export const WorkOrderWidget = ({
  title,
  settings,
  widgetSize,
}: WorkOrderWidgetProps) => {
  const [workOrders, setWorkOrders] = useState<WorkOrderWidgetList[]>([]);
  const [pageIndex, setPageIndex] = useState(0);

  const pageSize = useMemo(() => (widgetSize === "M" ? 10 : 5), [widgetSize]);

  const widgetSettings = useMemo(
    () => ({
      ...getWorkOrderWidgetSettings(settings),
      pageSize,
      pageIndex,
    }),
    [settings, pageSize, pageIndex]
  );

  const { data, isValidating } = useWorkOrderSearch(widgetSettings);

  useEffect(() => {
    if (data?.content) {
      setWorkOrders((prevWorkOrders) =>
        mapInstallationQuotationData(prevWorkOrders, data.content)
      );
    }
  }, [data]);

  const loadMoreQuotations = () => {
    setPageIndex((prev) => prev + 1);
  };

  const totalItems = data?.totalElements ?? 0;

  const widgetHeight =
    widgetSize === "M" ? CARD_HEIGHT_TALL : CARD_HEIGHT_NORMAL;

  return (
    <WidgetContainer size={widgetHeight}>
      <ActionTitle title={`${title} (${workOrders.length})`} />

      {!isValidating && workOrders.length === 0 ? (
        <Text>{t("prompt.no_work_orders")}</Text>
      ) : (
        <>
          <ScrollContainer>
            <WorkOrdersList workOrders={workOrders} />
          </ScrollContainer>

          {workOrders.length < totalItems && (
            <CompactPagination
              setSize={loadMoreQuotations}
              size={pageSize}
              totalNumber={totalItems}
              currentNumber={workOrders.length}
            />
          )}
        </>
      )}
    </WidgetContainer>
  );
};
